import classNames from "classnames";
import React, { FC, useEffect, useRef, useState } from "react";
import { LuArrowRight, LuArrowUp, LuSparkles, LuX } from "react-icons/lu";
import { useNavigate } from "react-router-dom";

import { getLinkedFirm } from "../../../sessionStorage/sessionStorage";
import {
  AiReportAskResponse,
  AiReportData,
  AiReportTable,
  useAiReportAskMutation,
} from "../../../slices/AiSlice";
import { aiErrorMessage } from "../../ai/ai.utils";
import { HelpReplyText } from "../../ai/help/HelpReplyText";
import { formatValue } from "../custom/reportEngine";
import { capReportData } from "./reportAsk.utils";

type Turn = {
  id: number;
  question: string;
  state:
    | { status: "thinking" }
    | { status: "error"; message: string }
    | { status: "done"; answer: AiReportAskResponse };
};

const cellText = (kind: string, value: string | number | null | undefined) => {
  if (value === null || value === undefined) return "—";
  if (typeof value === "number") {
    if (kind === "money" || kind === "hours" || kind === "percent")
      return formatValue(kind, value);
    if (kind === "number") return value.toLocaleString("en-US");
  }
  return String(value);
};

/** The rows the answer is about (cells copied from the report itself). */
const AnswerTable: FC<{
  table: AiReportTable;
  onOpen: (link: string) => void;
}> = ({ table, onOpen }) => (
  <div className={"overflow-x-auto rounded-[10px] border border-[#EDEFF3]"}>
    <table className={"w-full border-collapse text-[12.5px]"}>
      <thead>
        <tr>
          {table.columns.map((c, i) => (
            <th
              key={c.key}
              className={classNames(
                "whitespace-nowrap border-b border-[#EDEFF3] px-2.5 py-2 text-[10.5px] font-bold uppercase tracking-[0.04em] text-[#9097A8]",
                i && c.kind !== "text" && c.kind !== "date"
                  ? "text-right"
                  : "text-left",
              )}>
              {c.label}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {table.rows.map((row) => (
          <tr key={row.key}>
            {table.columns.map((c, i) => (
              <td
                key={c.key}
                className={classNames(
                  "border-b border-[#EDEFF3] px-2.5 py-2",
                  i && c.kind !== "text" && c.kind !== "date"
                    ? "text-right text-[#3A4256]"
                    : "text-left",
                  i === 0 && "font-bold text-[#1F2433]",
                )}>
                {i === 0 && row.link ? (
                  <button
                    type={"button"}
                    onClick={() => row.link && onOpen(row.link)}
                    className={"font-bold text-[#6B4EFF] hover:underline"}>
                    {cellText(c.kind, row.cells[c.key])}
                  </button>
                ) : (
                  cellText(c.kind, row.cells[c.key])
                )}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

/**
 * "Ask about this report" side panel (owner 2026-10-25). Answers come only from what the
 * report shows (`data`, with the current filters); "Show only these rows" narrows the report.
 */
export const ReportAskPanel: FC<{
  data: AiReportData;
  suggestions?: string[];
  onClose: () => void;
  onFocus: (table: string, keys: string[]) => void;
}> = ({ data, suggestions = [], onClose, onFocus }) => {
  const navigate = useNavigate();
  const [ask] = useAiReportAskMutation();
  const [turns, setTurns] = useState<Turn[]>([]);
  const [text, setText] = useState("");
  const counter = useRef(0);
  const bottom = useRef<HTMLDivElement>(null);
  const busy = turns.some((t) => t.state.status === "thinking");

  useEffect(() => {
    bottom.current?.scrollIntoView?.({ block: "end" });
  }, [turns]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const send = async (question: string) => {
    const q = question.trim();
    if (!q || busy) return;
    const id = (counter.current += 1);
    const history = turns
      .filter((t) => t.state.status === "done")
      .slice(-5)
      .map((t) => ({
        question: t.question,
        answer: t.state.status === "done" ? t.state.answer.answer : "",
      }));
    setTurns((all) => [
      ...all,
      { id, question: q, state: { status: "thinking" } },
    ]);
    setText("");
    try {
      const answer = await ask({
        orgId: getLinkedFirm()?.orgId || "",
        question: q,
        history,
        report: capReportData(data),
      }).unwrap();
      setTurns((all) =>
        all.map((t) =>
          t.id === id ? { ...t, state: { status: "done", answer } } : t,
        ),
      );
    } catch (error) {
      setTurns((all) =>
        all.map((t) =>
          t.id === id
            ? {
                ...t,
                state: { status: "error", message: aiErrorMessage(error) },
              }
            : t,
        ),
      );
    }
  };

  return (
    <aside
      aria-label={`Ask about ${data.title}`}
      className={
        "flex max-h-[calc(100vh-120px)] flex-col overflow-hidden rounded-[16px] border border-[#E4E7EC] bg-white shadow-[0_12px_32px_rgba(16,24,40,0.10)] lg:sticky lg:top-4"
      }>
      <div
        className={
          "flex items-center gap-2 border-b border-[#EDEFF3] px-4 py-3.5"
        }>
        <span
          className={
            "flex h-[26px] w-[26px] items-center justify-center rounded-lg bg-[#F1EEFF] text-[#6B4EFF]"
          }>
          <LuSparkles size={14} />
        </span>
        <span className={"min-w-0 flex-1 truncate text-[15px] font-extrabold"}>
          Ask about {data.title}
        </span>
        <button
          type={"button"}
          onClick={onClose}
          aria-label={"Close"}
          className={"text-[#9097A8] hover:text-[#1F2433]"}>
          <LuX size={18} />
        </button>
      </div>
      <div
        className={
          "flex-1 space-y-3 overflow-y-auto px-4 py-3.5 text-[13.5px] leading-[1.5]"
        }>
        <div className={"text-[11.5px] text-[#9097A8]"}>
          Answers use only what this report shows, with your filters.
        </div>
        {!turns.length && suggestions.length > 0 && (
          <div className={"flex flex-wrap gap-1.5"}>
            {suggestions.map((s) => (
              <button
                key={s}
                type={"button"}
                onClick={() => send(s)}
                className={
                  "rounded-full border border-[#D9D1FF] bg-[#F5F3FF] px-2.5 py-1 text-[12px] font-semibold text-[#6B4EFF] hover:bg-[#ECE7FF]"
                }>
                {s}
              </button>
            ))}
          </div>
        )}
        {turns.map((turn) => (
          <div key={turn.id} className={"space-y-2.5"}>
            <div
              className={
                "ml-auto w-fit max-w-[85%] rounded-[14px_14px_4px_14px] bg-[#6B4EFF] px-3 py-2 font-medium text-white"
              }>
              {turn.question}
            </div>
            {turn.state.status === "thinking" && (
              <div
                className={
                  "animate-pulse text-[13px] font-semibold text-[#6B7185]"
                }>
                Thinking…
              </div>
            )}
            {turn.state.status === "error" && (
              <div
                role={"alert"}
                className={
                  "rounded-[10px] border border-[#F6D2D2] bg-[#FFF5F5] px-3 py-2 text-[13px] text-[#B42318]"
                }>
                {turn.state.message}
              </div>
            )}
            {turn.state.status === "done" && (
              <>
                <div className={"text-[#1F2433]"}>
                  <HelpReplyText text={turn.state.answer.answer} />
                </div>
                {turn.state.answer.table &&
                  turn.state.answer.table.rows.length > 0 && (
                    <AnswerTable
                      table={turn.state.answer.table}
                      onOpen={(link) => navigate(link)}
                    />
                  )}
                {turn.state.answer.table &&
                  turn.state.answer.focusKeys.length > 0 && (
                    <button
                      type={"button"}
                      onClick={() => {
                        const { answer } = turn.state as {
                          answer: AiReportAskResponse;
                        };
                        if (answer.table)
                          onFocus(answer.table.name, answer.focusKeys);
                      }}
                      className={
                        "inline-flex items-center gap-1 text-[12.5px] font-bold text-[#6B4EFF] hover:underline"
                      }>
                      Show only{" "}
                      {turn.state.answer.focusKeys.length === 1
                        ? "this row"
                        : `these ${turn.state.answer.focusKeys.length} rows`}{" "}
                      in the report
                      <LuArrowRight size={13} />
                    </button>
                  )}
              </>
            )}
          </div>
        ))}
        {turns.length > 0 && (
          <div className={"text-[11.5px] text-[#9097A8]"}>
            AI answer from your firm&apos;s data. It can make mistakes.
          </div>
        )}
        <div ref={bottom} />
      </div>
      <form
        className={
          "m-3 mt-0 flex items-end gap-2 rounded-[12px] border border-[#E4E7EC] px-3 py-2 focus-within:border-[#6B4EFF]"
        }
        onSubmit={(e) => {
          e.preventDefault();
          send(text);
        }}>
        <textarea
          aria-label={"Ask a question about this report"}
          value={text}
          rows={1}
          maxLength={500}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              send(text);
            }
          }}
          placeholder={
            turns.length
              ? "Ask another question about this report…"
              : "Ask a question about this report…"
          }
          className={
            "max-h-24 min-h-[28px] flex-1 resize-none border-0 bg-transparent py-1 text-[13.5px] outline-none placeholder:text-[#9097A8]"
          }
        />
        <button
          type={"submit"}
          disabled={busy || !text.trim()}
          aria-label={"Send"}
          className={
            "flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-[#6B4EFF] text-white disabled:opacity-40"
          }>
          <LuArrowUp size={15} />
        </button>
      </form>
    </aside>
  );
};
